import { Head, Link } from '@inertiajs/react';

import {
    Btn,
    DesignFooter,
    DesignHeader,
    I,
    LawyerCardV,
    SectionHeader,
    UtilityBar,
    type LawyerCardData,
} from '@/Components/Design';

type Props = {
    city: {
        slug: string;
        name: string;
        population: number | null;
        lat: number | null;
        lng: number | null;
        state: { slug: string; name: string; abbreviation: string };
        lawyersCount: number;
    };
    lawyers: Array<{
        id: number;
        slug: string;
        name: string;
        firm: string | null;
        city: string | null;
        state: string | null;
        stateAbbr: string | null;
        yearsLicensed: number | null;
        languages: string[];
        reviewsAvg: number | null;
        reviewsCount: number;
        practiceAreas: Array<{ slug: string; name: string }>;
        featured: boolean;
        headshotKey: string | null;
    }>;
};

function initialsOf(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    return ((parts[0]?.[0] ?? '') + (parts[parts.length - 1]?.[0] ?? '')).toUpperCase() || 'AN';
}

function hueOf(slug: string): number {
    let h = 0;
    for (let i = 0; i < slug.length; i++) h = (h * 31 + slug.charCodeAt(i)) | 0;
    return Math.abs(h) % 6;
}

function toCard(l: Props['lawyers'][number], i: number): LawyerCardData {
    return {
        name: l.name,
        firm: l.firm ?? '',
        city: l.city ?? '',
        state: l.stateAbbr ?? '',
        rating: l.reviewsAvg ?? 0,
        reviews: l.reviewsCount,
        years: l.yearsLicensed ?? 0,
        areas: l.practiceAreas.map((p) => p.name),
        langs: l.languages,
        initials: initialsOf(l.name),
        hue: hueOf(l.slug) ?? i,
        featured: l.featured,
        href: `/${l.slug}`,
        headshotUrl: l.headshotKey ? `/cdn/lawyers/320/${l.headshotKey}.webp` : null,
    };
}

export default function City({ city, lawyers }: Props) {
    return (
        <>
            <Head>
                <title>{`Personal injury lawyers in ${city.name}, ${city.state.abbreviation}`}</title>
            </Head>
            <div style={{ background: 'var(--paper)', minHeight: '100vh' }}>
                <UtilityBar />
                <DesignHeader active="By State" />

                <section data-city-section="hero" style={{ maxWidth: 1440, margin: '0 auto', padding: '40px 56px 32px' }}>
                    <nav aria-label="Breadcrumb">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--ink-mute)', marginBottom: 18 }}>
                        <Link href="/" style={{ color: 'inherit', textDecoration: 'none' }}>Home</Link>
                        <span>/</span>
                        <Link href={`/${city.state.slug}`} style={{ color: 'inherit', textDecoration: 'none' }}>{city.state.name}</Link>
                        <span>/</span>
                        <span aria-current="page" style={{ color: 'var(--ink)' }}>{city.name}</span>
                    </div></nav>
                    <div data-city-hero-grid style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 48, alignItems: 'flex-end' }}>
                        <div>
                            <div style={{ fontSize: 12, letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--ember)', marginBottom: 10, fontWeight: 600 }}>City directory</div>
                            <h1 style={{ fontSize: 64, lineHeight: 1.02, letterSpacing: '-0.025em', marginBottom: 18, fontFamily: 'var(--font-serif)', fontWeight: 500 }}>
                                Personal injury lawyers in {city.name}, {city.state.abbreviation}
                            </h1>
                            <p style={{ fontSize: 18, color: 'var(--ink-soft)', lineHeight: 1.55, maxWidth: 580, marginBottom: 24 }}>
                                {city.lawyersCount > 0
                                    ? `${city.lawyersCount.toLocaleString()} bar-verified personal injury attorney${city.lawyersCount === 1 ? '' : 's'} serving ${city.name} and the surrounding area.`
                                    : `Bar-verified personal injury attorneys serving ${city.name}.`}
                            </p>
                            <div data-cta-row style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                                <Btn kind="primary" size="lg" icon={I.phone} href="/get-matched">Get matched</Btn>
                                <Btn kind="secondary" size="lg" icon={I.search} href={`/${city.state.slug}`}>Back to {city.state.name}</Btn>
                            </div>
                        </div>
                        <div data-city-glyph style={{ background: 'var(--card)', border: '1px solid var(--rule)', borderRadius: 16, padding: 24, textAlign: 'center' }}>
                            <div style={{ fontFamily: 'var(--font-serif)', fontSize: 36, fontWeight: 500, lineHeight: 1.1, letterSpacing: '-0.02em' }}>{city.name}</div>
                            <div style={{ fontSize: 14, color: 'var(--ember)', marginTop: 4, fontWeight: 600 }}>{city.state.abbreviation}</div>
                            {city.population && city.population > 0 && (
                                <div style={{ fontSize: 12, color: 'var(--ink-mute)', marginTop: 12 }}>
                                    Population <span className="pip-mono" style={{ color: 'var(--ink)' }}>{city.population.toLocaleString()}</span>
                                </div>
                            )}
                        </div>
                    </div>
                </section>

                {lawyers.length > 0 ? (
                    <section data-city-section="lawyers" style={{ maxWidth: 1440, margin: '0 auto', padding: '32px 56px 64px' }}>
                        <SectionHeader
                            eyebrow={`In ${city.name}`}
                            title="Available attorneys"
                            action={<Btn kind="text" href={`/search?state=${city.state.slug}&city=${city.slug}`}>{`All ${city.lawyersCount} →`}</Btn>}
                        />
                        <div data-city-lawyers-grid style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 }}>
                            {lawyers.map((l, i) => (
                                <LawyerCardV key={l.id} data={toCard(l, i)} />
                            ))}
                        </div>
                    </section>
                ) : (
                    <section style={{ maxWidth: 1200, margin: '0 auto', padding: '32px 56px 64px' }}>
                        <div style={{ background: 'var(--card)', border: '1px solid var(--rule)', borderRadius: 12, padding: 32, textAlign: 'center' }}>
                            <div style={{ fontSize: 18, color: 'var(--ink-soft)', marginBottom: 8 }}>No attorneys listed in {city.name} yet.</div>
                            <Link href={`/${city.state.slug}`} style={{ color: 'var(--ember)', fontSize: 14, fontWeight: 600 }}>Browse all {city.state.name} attorneys →</Link>
                        </div>
                    </section>
                )}

                <DesignFooter />
            </div>
        </>
    );
}
